import { useState } from "react";

import { format } from "date-fns";

import Markdown from "react-markdown";

// Components
import { Button } from "../ui/button.tsx";
import { Badge } from "../ui/badge.tsx";
import { Input } from "../ui/input.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.tsx";
import { HeaderTypes } from "./Header.tsx";

import { DownloadIcon, Plus, X } from "lucide-react";

import { cn } from "../../lib/utils.js";

import { useApi } from "../../Api/useApi.ts";

import { useParams } from "react-router-dom";

import { useMutation } from "@tanstack/react-query";

export type CellTypes = HeaderTypes;

type DefaultCellTypes = "tags" | "date";

type CellProps = {
    data: string[] | string;
    type: DefaultCellTypes | CellTypes;
    onClick: () => void;
    responseId?: string;
};

function Cell({ data, type, onClick, responseId }: CellProps) {
    if (type === "status") return <Status status={data as any} />;

    /*   if (type === "tags")
        return <Tags tags={[]} onAdd={() => {}} onDelete={() => {}} />; */

    if (type === "date") return <DateCell date={data as string} />;

    if (type === "file_upload")
        return <FileUpload files={data as any[]} responseId={responseId} />;

    const answersArr = Array.isArray(data) ? data : [data];
    const answers = answersArr.join(", ");

    if (type === "ai_statement") return <AIStatement text={answers} />;

    if (type === "picture_choice")
        return <PictureChoiceCell answers={data as any[]} />;

    return <p className="max-w-[400px]">{answers}</p>;
}

export default Cell;

/* type HeaderVariantProps = Pick<CellProps, "data" | "onClick">;

type TagsProps = {
    tags: string[];
    onAdd: (tag: string) => void;
    onDelete: (tag: string) => void;
};

function Tags({ tags: _tags, onAdd, onDelete }: TagsProps) {
    const [tags, setTags] = useState<string[]>(_tags);
    const [tag, setTag] = useState("");
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen} modal={false}>
            <PopoverTrigger
                asChild
                autoFocus={false}
                className="relative flex flex-row flex-wrap w-full h-full gap-1 py-0 my-0 border-none outline-none"
            >
                <div className="max-w-xs">
                    {tags.length === 0 && (
                        <Button
                            variant="link"
                            onClick={() => onAdd(tag)}
                            className="h-full text-xs text-gray-500 w-fit hover:no-underline"
                        >
                            <Plus className="w-3 h-3 mr-1" /> Add tag
                        </Button>
                    )}
                    {tags.map((tag) => (
                        <Tag tag={tag} />
                    ))}
                </div>
            </PopoverTrigger>
            <PopoverContent className="absolute -top-full -left-[150px] flex flex-row flex-wrap gap-1 p-2">
                {tags.map((tag) => (
                    <Tag
                        tag={tag}
                        onDelete={() => {
                            setTags(tags.filter((t) => t !== tag));
                            onDelete(tag);
                        }}
                    />
                ))}
                <Input
                    autoFocus={true}
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && tag.trim()) {
                            setTags([...tags, tag]);
                            setTag("");
                            onAdd(tag);
                        }
                    }}
                    className="w-20 h-5 px-1 border-none rounded-none outline-none focus:outline-none focus:border-none focus-visible:ring-transparent"
                />
            </PopoverContent>
        </Popover>
    );
}

type TagProps = {
    tag: string;
    onDelete?: (tag: string) => void;
};

function Tag({ tag, onDelete }: TagProps) {
    return (
        <Badge variant="outline" className="rounded-md">
            {tag}
            {onDelete && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="w-5 h-5 ml-1"
                    onClick={() => onDelete(tag)}
                >
                    <X className="w-3 h-3" />
                </Button>
            )}
        </Badge>
    );
} */

type DateCellProps = {
    date: string;
};

function DateCell({ date }: DateCellProps) {
    const formattedDate = format(date, "dd LLL yyyy");

    return (
        <div>
            <p>{formattedDate}</p>
        </div>
    );
}

type StatusCellProps = {
    status: "completed" | "partial" | "started";
};

function Status({ status }: StatusCellProps) {
    if (status === "started")
        return (
            <Badge
                variant="outline"
                className="px-2 py-1 text-xs font-normal text-gray-600 bg-gray-100 rounded-sm"
            >
                Started
            </Badge>
        );

    if (status === "partial")
        return (
            <Badge
                variant="outline"
                className="px-2 py-1 text-xs font-normal text-gray-600 bg-gray-100 rounded-sm"
            >
                Partial
            </Badge>
        );

    if (status === "completed")
        return (
            <Badge
                variant="outline"
                className="text-xs text-[#1E40AF] rounded-sm font-normal bg-[#DBEAFE] py-1 px-2"
            >
                Completed
            </Badge>
        );

    return null;
}

type FileUploadProps = {
    files: any[];
    responseId?: string;
};

function FileUpload({ files, responseId }: FileUploadProps) {
    const api = useApi();
    const { urlId } = useParams();

    const getName = () => {
        if (files.length === 0) return "No uploads";
        if (files.length === 1) return files[0]?.file_name;
        return `${files.length} Documents`;
    };

    //download file
    const downloadFile = (file: any) => {
        const link = document.createElement("a");
        link.href = file.file_download_url;
        link.setAttribute("download", file.file_name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getViewLinkMutation = useMutation({
        mutationFn: ({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        }: {
            fileId: string;
            formId: string;
            responseId: string;
            fieldId: string;
            contentViewMethod: string;
        }) =>
            api.files.viewFile(
                fileId,
                formId,
                responseId,
                fieldId,
                contentViewMethod,
            ),
        onSuccess: (data: any) => {
            // Open the file in a new tab
            const fileUrl = data?.data?.url;
            if (fileUrl) {
                const newTab = window.open(fileUrl, "_blank");
                if (newTab) {
                    newTab.focus();
                } else {
                    console.error("");
                }
            } else {
                console.error("");
            }
        },
        onError: (error: any) => {
            console.error("Could not get file", error);
        },
    });

    const handleOnView = (file: any) => {
        const fileId = file?.id;
        const id = responseId as string;
        const formId = urlId as string;
        const fieldId = file?.field_id;
        const contentViewMethod = "inline";

        getViewLinkMutation.mutate({
            fileId,
            formId,
            responseId: id,
            fieldId,
            contentViewMethod,
        });
    };

    const getDownloadLink = useMutation({
        mutationFn: ({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        }: {
            fileId: string;
            formId: string;
            responseId: string;
            fieldId: string;
            contentViewMethod: string;
        }) =>
            api.files.viewFile(
                fileId,
                formId,
                responseId,
                fieldId,
                contentViewMethod,
            ),
        onSuccess: (data: any) => {
            // Open the file in a new tab
            const fileUrl = data?.data?.url;
            if (fileUrl) {
                const link = document.createElement("a");
                link.href = fileUrl;
                link.setAttribute("download", data?.data?.file_name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error("");
            }
        },
        onError: (error: any) => {
            console.error("Could not get file", error);
        },
    });

    const handleDownload = (file: any) => {
        const fileId = file?.id;
        const id = responseId as string;
        const formId = urlId as string;
        const fieldId = file?.field_id;
        const contentViewMethod = "attachment";

        getDownloadLink.mutate({
            fileId,
            formId,
            responseId: id,
            fieldId,
            contentViewMethod,
        });
    };

    return (
        <div className="flex flex-row items-center justify-between gap-2">
            {files.length === 1 ? (
                <button
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleOnView(files[0]);
                    }}
                    className="text-xs "
                >
                    {getName()}
                </button>
            ) : (
                <p
                    className={cn(
                        "text-xs",
                        files.length === 0 && "text-gray-400",
                    )}
                >
                    {getName()}
                </p>
            )}

            {files?.length === 1 ? (
                <Button
                    variant="ghost"
                    size="sm"
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDownload(files[0]);
                    }}
                >
                    <DownloadIcon className="w-3 h-3" />
                </Button>
            ) : (
                <Button variant="ghost" size="sm" disabled={files.length === 0}>
                    <DownloadIcon className="w-3 h-3" />
                </Button>
            )}
        </div>
    );
}

type AIStatementProps = {
    text: string;
};

function AIStatement({ text }: AIStatementProps) {
    return (
        <Markdown className="w-full max-w-xl break-words min-w-[10rem]">
            {text}
        </Markdown>
    );
}

function PictureChoiceCell({ answers }: any) {
    return (
        <div className="flex flex-wrap items-center w-full gap-1 ">
            {answers.map((answer: any, index: number) => (
                <div
                    key={index}
                    className="w-[89px] h-6 gap-1 flex items-center border border-[#DCDFE4] rounded-md"
                >
                    <img
                        src={answer.url}
                        alt={answer.answer_text}
                        className="object-contain size-6 rounded-tl-md rounded-bl-md"
                    />
                    <span className="text-xs truncate">
                        {answer.answer_text}
                    </span>
                </div>
            ))}
        </div>
    );
}
